import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * RouterSpecsGrid component
 * Displays the router specifications in a grid layout
 */
const RouterSpecsGrid = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };

  return (
    <div className="mb-8">
      <h3 className="text-2xl font-bold text-white mb-6">Our Free-to-Use Routers</h3>
      
      {/* Router Selection Info */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 mb-8">
        <p className="text-gray-300 mb-4">
          We provide different router models based on your internet package speed:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-700/50 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold text-lg mb-2">100Mbps and Below</h4>
            <p className="text-gray-300">EX141 AX1500 Dual Band Gigabit Wi-Fi 6 Router</p>
          </div>
          <div className="bg-gray-700/50 rounded-lg p-4">
            <h4 className="text-purple-400 font-semibold text-lg mb-2">Above 100Mbps</h4>
            <p className="text-gray-300">Elements Mesh Dual Band WiFi 6 Router</p>
          </div>
        </div>
      </div>
      
      {/* Main Specs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 hover:border-purple-500/50 transition-colors">
          <h4 className="text-purple-400 font-semibold text-lg mb-3">Dual-Band WiFi 6</h4>
          <p className="text-gray-300 mb-4">Enjoy faster browsing, streaming, and downloading across two bands with the latest WiFi 6 technology.</p>
          <div className="flex justify-between text-sm">
            <span className="text-purple-300">5 GHz</span>
            <span className="text-white font-medium">1201 Mbps</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2 mt-1 mb-3">
            <div className="bg-purple-500 h-2 rounded-full" style={{ width: '100%' }}></div>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-purple-300">2.4 GHz</span>
            <span className="text-white font-medium">300 Mbps</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2 mt-1">
            <div className="bg-purple-500 h-2 rounded-full" style={{ width: '25%' }}></div>
          </div>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 hover:border-purple-500/50 transition-colors">
          <h4 className="text-purple-400 font-semibold text-lg mb-3">Boosted Coverage</h4>
          <p className="text-gray-300">Four high-performance antennas and Beamforming technology combine to extend strong, reliable WiFi throughout your home.</p>
          <div className="mt-4 flex justify-center">
            <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="60" cy="60" r="30" fill="#6B46C1" fillOpacity="0.3" />
              <circle cx="60" cy="60" r="45" stroke="#9F7AEA" strokeWidth="2" strokeDasharray="4 4" />
              <circle cx="60" cy="60" r="60" stroke="#9F7AEA" strokeWidth="1" strokeDasharray="2 2" />
              <circle cx="60" cy="60" r="10" fill="#9F7AEA" />
            </svg>
          </div>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 hover:border-purple-500/50 transition-colors">
          <h4 className="text-purple-400 font-semibold text-lg mb-3">More Connections</h4>
          <p className="text-gray-300">OFDMA and MU-MIMO technologies increase capacity and enable simultaneous transmission to more devices.</p>
          <div className="mt-4 flex justify-center">
            <div className="grid grid-cols-3 gap-2">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="w-6 h-6 rounded-full bg-purple-500 opacity-80"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Secondary Specs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 hover:border-purple-500/50 transition-colors">
          <h4 className="text-purple-400 font-semibold text-lg mb-3">Wi-Fi CERTIFIED EasyMesh™</h4>
          <p className="text-gray-300">Create a smart mesh network with seamless coverage and greater flexibility based on standard mesh technology.</p>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 hover:border-purple-500/50 transition-colors">
          <h4 className="text-purple-400 font-semibold text-lg mb-3">Enhanced Security</h4>
          <p className="text-gray-300">WPA3 encryption, Multi-SSID, and Parental Controls provide personalized and safer online experience.</p>
        </div>
      </div>
      
      {/* Tertiary Specs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 hover:border-purple-500/50 transition-colors">
          <h4 className="text-purple-400 font-semibold text-lg mb-3">Gigabit Wired Connections</h4>
          <p className="text-gray-300">1x GE WAN port and 3x GE LAN ports provide fast and flexible internet access.</p>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 hover:border-purple-500/50 transition-colors">
          <h4 className="text-purple-400 font-semibold text-lg mb-3">Easy Setup</h4>
          <p className="text-gray-300">Set up your network in minutes with the powerful Aginet app.</p>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 hover:border-purple-500/50 transition-colors">
          <h4 className="text-purple-400 font-semibold text-lg mb-3">Remote Management</h4>
          <p className="text-gray-300">TAUC (TP-Link Aginet Unified Cloud), and TR-069, TR-098, TR-181, TR-111, TR-143 protocols are supported.</p>
        </div>
      </div>
      
      {/* Detailed Specifications (Expandable) */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden">
        <div 
          className="p-6 flex items-center justify-between cursor-pointer"
          onClick={() => toggleSection('detailed')}
        >
          <h4 className="text-xl font-semibold text-white">Detailed Technical Specifications</h4>
          {expandedSection === 'detailed' ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
        
        {expandedSection === 'detailed' && (
          <div className="px-6 pb-6 border-t border-gray-700/50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4">
              <div>
                <h5 className="text-purple-400 font-medium mb-3">Hardware Features</h5>
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b border-gray-700/30">
                      <td className="py-2 pr-4 text-gray-400 align-top">Interface</td>
                      <td className="py-2 text-gray-300">1 10/100/1000 Mbps RJ45 WAN Port<br />3 10/100/1000 Mbps RJ45 LAN Ports</td>
                    </tr>
                    <tr className="border-b border-gray-700/30">
                      <td className="py-2 pr-4 text-gray-400 align-top">Button</td>
                      <td className="py-2 text-gray-300">Reset Button, WPS/Wi-Fi Button</td>
                    </tr>
                    <tr className="border-b border-gray-700/30">
                      <td className="py-2 pr-4 text-gray-400 align-top">External Power Supply</td>
                      <td className="py-2 text-gray-300">12V/1A</td>
                    </tr>
                    <tr className="border-b border-gray-700/30">
                      <td className="py-2 pr-4 text-gray-400 align-top">Dimensions</td>
                      <td className="py-2 text-gray-300">7.2 × 3.7 × 1.3 in (182.7 × 94 × 33.3 mm)</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 text-gray-400 align-top">Antenna</td>
                      <td className="py-2 text-gray-300">Four External antennas</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div>
                <h5 className="text-purple-400 font-medium mb-3">Wireless Features</h5>
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b border-gray-700/30">
                      <td className="py-2 pr-4 text-gray-400 align-top">Wireless Standards</td>
                      <td className="py-2 text-gray-300">IEEE 802.11ax/ac/n/a 5 GHz, IEEE 802.11n/g/b 2.4 GHz</td>
                    </tr>
                    <tr className="border-b border-gray-700/30">
                      <td className="py-2 pr-4 text-gray-400 align-top">Frequency</td>
                      <td className="py-2 text-gray-300">2.4 GHz and 5 GHz</td>
                    </tr>
                    <tr className="border-b border-gray-700/30">
                      <td className="py-2 pr-4 text-gray-400 align-top">Signal Rate</td>
                      <td className="py-2 text-gray-300">1201 Mbps on 5 GHz, 300 Mbps on 2.4 GHz</td>
                    </tr>
                    <tr className="border-b border-gray-700/30">
                      <td className="py-2 pr-4 text-gray-400 align-top">Wireless Functions</td>
                      <td className="py-2 text-gray-300">Enable/Disable Wireless Radio, WMM, Wireless Statistics, Band Steering</td>
                    </tr>
                    <tr className="border-b border-gray-700/30">
                      <td className="py-2 pr-4 text-gray-400 align-top">Wireless Security</td>
                      <td className="py-2 text-gray-300">WPA/WPA2/WPA3 encryptions</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 text-gray-400 align-top">Mesh Technology</td>
                      <td className="py-2 text-gray-300">EasyMesh Compatible, and support 802.11k/v/r</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="mt-6">
              <h5 className="text-purple-400 font-medium mb-3">Software Features</h5>
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b border-gray-700/30">
                    <td className="py-2 pr-4 text-gray-400 align-top">Advanced Features</td>
                    <td className="py-2 text-gray-300">Muti-SSID, Guest Network, QoS (Quality of Service), TX Beamforming, MU-MIMO, OFDMA, Airtime Fairness</td>
                  </tr>
                  <tr className="border-b border-gray-700/30">
                    <td className="py-2 pr-4 text-gray-400 align-top">Operating Modes</td>
                    <td className="py-2 text-gray-300">Router, Access Point</td>
                  </tr>
                  <tr className="border-b border-gray-700/30">
                    <td className="py-2 pr-4 text-gray-400 align-top">WAN Type</td>
                    <td className="py-2 text-gray-300">Dynamic IP/Static IP/PPPoE/PPTP/L2TP (Multi-EWAN)</td>
                  </tr>
                  <tr className="border-b border-gray-700/30">
                    <td className="py-2 pr-4 text-gray-400 align-top">Management Features</td>
                    <td className="py-2 text-gray-300">Local management: WebGUI management, Aginet app management<br />Remote management: Aginet ACS, TR-069, TR-098, TR-181,TR-111, and TR-143 for service providers</td>
                  </tr>
                  <tr className="border-b border-gray-700/30">
                    <td className="py-2 pr-4 text-gray-400 align-top">Access Control</td>
                    <td className="py-2 text-gray-300">Parental Controls, White List, Black List</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 text-gray-400 align-top">Firewall Security</td>
                    <td className="py-2 text-gray-300">DoS, SPI Firewall, IP and MAC Address Binding</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RouterSpecsGrid;